import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarCheck, Check, Clock, Link2, LogOut, MessageSquare, Unplug } from "lucide-react";
import { DEFAULT_AVAILABILITY, fmtHour } from "@/lib/booking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.22em] text-smoke";
const inputCls =
  "w-full border border-hairline bg-white px-4 py-3 text-[15px] text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-ink focus:ring-1 focus:ring-ink";

function SetupSteps({ title, steps }) {
  return (
    <div className="mt-4 border border-hairline bg-ivory p-5 text-sm leading-relaxed text-smoke break-words [overflow-wrap:anywhere]">
      <p className="mb-2 font-semibold text-ink">{title}</p>
      <ol className="list-decimal space-y-1 pl-5">
        {steps.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>
    </div>
  );
}

function Card({ icon, title, children }) {
  return (
    <div className="border border-hairline bg-white p-7 sm:p-9" data-testid="admin-card">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center bg-ink text-ivory">{icon}</span>
        <h2 className="font-serif text-2xl text-ink">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(sessionStorage.getItem("chk_owner_token") || "");
  const [code, setCode] = useState("");
  const [loginError, setLoginError] = useState("");
  const [availability, setAvailability] = useState(DEFAULT_AVAILABILITY);
  const [integrations, setIntegrations] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [savedFlash, setSavedFlash] = useState("");
  const [googleBanner, setGoogleBanner] = useState(new URLSearchParams(window.location.search).get("google"));

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadAll = async (t = token) => {
    const h = { headers: { Authorization: `Bearer ${t}` } };
    try {
      const [av, integ, bk] = await Promise.all([
        axios.get(`${API}/availability`),
        axios.get(`${API}/admin/integrations`, h),
        axios.get(`${API}/admin/bookings`, h),
      ]);
      setAvailability(av.data);
      setIntegrations(integ.data);
      setBookings(bk.data);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem("chk_owner_token");
        setToken("");
      }
    }
  };

  useEffect(() => {
    if (token) loadAll(token);
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { code });
      sessionStorage.setItem("chk_owner_token", data.token);
      setToken(data.token);
      loadAll(data.token);
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Incorrect access code");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("chk_owner_token");
    setToken("");
  };

  const toggleDay = (d) => {
    setAvailability((a) => ({
      ...a,
      weekdays: a.weekdays.includes(d) ? a.weekdays.filter((x) => x !== d) : [...a.weekdays, d],
    }));
  };

  const saveAvailability = async () => {
    try {
      const { data } = await axios.put(`${API}/admin/availability`, availability, auth);
      setAvailability(data);
      setSavedFlash("Availability saved — the booking calendar now shows these hours.");
      setTimeout(() => setSavedFlash(""), 4000);
    } catch (err) {
      setSavedFlash(err.response?.data?.detail || "Could not save availability");
    }
  };

  const connectGoogle = async () => {
    try {
      const { data } = await axios.get(`${API}/oauth/calendar/login`, auth);
      window.location.href = data.authorization_url;
    } catch (err) {
      setSavedFlash(err.response?.data?.detail || "Could not start Google connection");
    }
  };

  const disconnectGoogle = async () => {
    await axios.post(`${API}/oauth/calendar/disconnect`, {}, auth);
    loadAll();
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory px-5">
        <form onSubmit={login} className="w-full max-w-sm border border-hairline bg-white p-9" data-testid="admin-login-card">
          <span className="flex h-11 w-11 items-center justify-center bg-ink font-serif text-[15px] tracking-[0.08em] text-ivory">
            CHK
          </span>
          <h1 className="mt-6 font-serif text-3xl text-ink">Owner Settings</h1>
          <p className="mt-2 text-sm font-light text-smoke">
            Enter your access code to manage availability, Google sync and SMS reminders.
          </p>
          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Access code"
            data-testid="admin-code-input"
            className={`${inputCls} mt-6`}
          />
          {loginError && (
            <p className="mt-3 text-sm text-red-700" data-testid="admin-login-error" role="alert">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-btn"
            className="mt-5 w-full bg-ink px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-ink/85"
          >
            Sign in
          </button>
        </form>
      </div>
    );
  }

  const g = integrations?.google;
  const sms = integrations?.sms;

  return (
    <div className="min-h-screen bg-ivory pb-20">
      <header className="border-b border-hairline bg-white">
        <div className="mx-auto flex h-[72px] max-w-5xl items-center justify-between px-5 sm:px-8">
          <span className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center bg-ink font-serif text-[15px] tracking-[0.08em] text-ivory">
              CHK
            </span>
            <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-ink">Owner Settings</span>
          </span>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 border border-ink px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-white"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-6 px-5 pt-10 sm:px-8">
        {googleBanner === "connected" && (
          <p className="border border-hairline bg-white px-5 py-4 text-sm text-ink" data-testid="google-connected-banner">
            Google Calendar connected — new bookings now appear on your calendar automatically.
          </p>
        )}
        {googleBanner === "error" && (
          <p className="border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700" data-testid="google-error-banner">
            Google connection failed — please try again.
          </p>
        )}
        {savedFlash && (
          <p className="border border-hairline bg-white px-5 py-4 text-sm text-ink" data-testid="admin-flash">
            {savedFlash}
          </p>
        )}

        <Card icon={<CalendarCheck size={17} />} title="Booking availability">
          <p className="text-sm font-light text-smoke">
            Choose which days and hours prospects can book. Changes apply to the public calendar instantly.
          </p>
          <span className={`${labelCls} mt-6`}>Days</span>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d, i) => {
              const active = availability.weekdays.includes(i);
              return (
                <button
                  key={d}
                  type="button"
                  data-testid={`weekday-toggle-${i}`}
                  onClick={() => toggleDay(i)}
                  className={[
                    "border px-4 py-2.5 text-[12px] font-medium transition-colors",
                    active ? "border-ink bg-ink text-white" : "border-hairline text-ink hover:border-ink",
                  ].join(" ")}
                >
                  {d}
                </button>
              );
            })}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="a-start" className={labelCls}>First slot</label>
              <select
                id="a-start"
                data-testid="select-start-hour"
                className={inputCls}
                value={availability.startHour}
                onChange={(e) => setAvailability((a) => ({ ...a, startHour: Number(e.target.value) }))}
              >
                {[8, 9, 10, 11, 12, 13, 14, 15].map((h) => (
                  <option key={h} value={h}>{fmtHour(h)}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="a-end" className={labelCls}>Last slot ends</label>
              <select
                id="a-end"
                data-testid="select-end-hour"
                className={inputCls}
                value={availability.endHour}
                onChange={(e) => setAvailability((a) => ({ ...a, endHour: Number(e.target.value) }))}
              >
                {[12, 13, 14, 15, 16, 17, 18, 19, 20, 21].map((h) => (
                  <option key={h} value={h}>{fmtHour(h)}</option>
                ))}
              </select>
            </div>
          </div>
          <button
            type="button"
            onClick={saveAvailability}
            data-testid="admin-availability-save"
            className="mt-7 bg-ink px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-ink/85"
          >
            Save availability
          </button>
        </Card>

        <Card icon={<Link2 size={17} />} title="Google Calendar sync">
          {g?.connected ? (
            <div data-testid="google-connected-state">
              <p className="flex items-center gap-2 text-sm text-ink">
                <Check size={15} className="text-green-700" />
                Connected as <strong>{g.email}</strong> — new bookings are added to your calendar and prospects receive a Google invite.
              </p>
              <button
                type="button"
                onClick={disconnectGoogle}
                data-testid="google-disconnect-btn"
                className="mt-5 flex items-center gap-2 border border-ink px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-white"
              >
                <Unplug size={13} /> Disconnect
              </button>
            </div>
          ) : g?.configured ? (
            <div data-testid="google-ready-state">
              <p className="text-sm font-light text-smoke">
                Credentials are set. Connect your Google account once — every new booking then appears on your calendar automatically.
              </p>
              <button
                type="button"
                onClick={connectGoogle}
                data-testid="google-connect-btn"
                className="mt-5 bg-ink px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-ink/85"
              >
                Connect Google account
              </button>
            </div>
          ) : (
            <div data-testid="google-setup-state">
              <p className="text-sm font-light text-smoke">
                Not set up yet. It takes about five minutes in Google Cloud Console:
              </p>
              <SetupSteps
                title="One-time setup"
                steps={[
                  "console.cloud.google.com → create a project → enable the Google Calendar API (APIs & Services → Library).",
                  "OAuth consent screen → External → add scope https://www.googleapis.com/auth/calendar → add will@corriehardingteam.com as a Test user.",
                  "Credentials → Create OAuth client ID → Web application → Authorized redirect URI: https://will-chk-realestate.preview.emergentagent.com/api/oauth/calendar/callback (add your live domain here after deploying too).",
                  "Send me the Client ID and Client Secret and I will add them to the backend settings.",
                ]}
              />
            </div>
          )}
        </Card>

        <Card icon={<MessageSquare size={17} />} title="SMS reminders">
          {sms?.configured ? (
            <p className="text-sm text-ink" data-testid="sms-on-state">
              Enabled — prospects automatically receive a text reminder about an hour before their consultation.
            </p>
          ) : (
            <div data-testid="sms-setup-state">
              <p className="text-sm font-light text-smoke">
                Not set up yet. Create a Twilio account (free trial works) and grab three values:
              </p>
              <SetupSteps
                title="One-time setup"
                steps={[
                  "Sign up at twilio.com → copy the Account SID and Auth Token from the console dashboard.",
                  "Buy (or claim a trial) Twilio phone number with SMS capability — this becomes the sender.",
                  "Send me the Account SID, Auth Token and Twilio phone number and I will switch reminders on.",
                ]}
              />
            </div>
          )}
        </Card>

        <Card icon={<Clock size={17} />} title="Upcoming consultations">
          {bookings.length === 0 ? (
            <p className="text-sm font-light text-smoke" data-testid="bookings-empty">
              No upcoming consultations yet.
            </p>
          ) : (
            <div className="overflow-x-auto" data-testid="bookings-table">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-hairline text-[10px] uppercase tracking-[0.2em] text-smoke">
                    <th className="py-3 pr-4">When (Toronto)</th>
                    <th className="py-3 pr-4">Name</th>
                    <th className="py-3 pr-4">Phone</th>
                    <th className="py-3">Address</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b border-hairline" data-testid="booking-row">
                      <td className="py-3 pr-4 whitespace-nowrap">{b.slot_display}</td>
                      <td className="py-3 pr-4 font-medium">{b.name}</td>
                      <td className="py-3 pr-4 whitespace-nowrap">{b.phone}</td>
                      <td className="py-3">{b.address}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}